import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Zap, Cpu, PlugZap, Lightbulb, ShieldCheck, DoorOpen, Network, Wrench } from "lucide-react";
import { Reveal, MaskedLine } from "./motion/Reveal";

const SERVICE_ICONS = [Zap, Cpu, PlugZap, Lightbulb, ShieldCheck, DoorOpen, Network, Wrench];
const SERVICE_KEYS = ["faults", "panel", "circuits", "lighting", "nen", "intercom", "data", "maintenance"];

export default function Services() {
  const { t } = useTranslation();
  return (
    <section id="diensten" data-testid="services-section" className="section bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="mb-14 md:mb-16 max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <span className="accent-bar" />
              <span className="text-[#FF5A1F] uppercase font-bold tracking-widest text-xs">
                {t("services.eyebrow")}
              </span>
            </div>
          </Reveal>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A1128] mb-5 leading-[1.02]">
            <span className="block overflow-hidden"><MaskedLine text={t("services.title_1")} wordDelay={0.06} /></span>
            <span className="block overflow-hidden"><MaskedLine text={t("services.title_2")} wordDelay={0.05} delay={0.15} /></span>
          </h2>
          <Reveal delay={0.15}>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
              {t("services.subtitle")}
            </p>
          </Reveal>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px 0px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6"
        >
          {SERVICE_KEYS.map((k, i) => {
            const Icon = SERVICE_ICONS[i];
            return (
              <motion.div
                key={k}
                data-testid={`service-card-${i}`}
                variants={{ hidden: { opacity: 0, y: 32 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } }}
                whileHover={{ y: -6 }}
                className="group relative bg-white border border-slate-200 p-7 flex flex-col gap-4 hover:border-[#0A1128] hover:shadow-[8px_8px_0_0_rgba(10,17,40,0.08)] transition-[border-color,box-shadow] duration-300"
              >
                <div className="absolute top-0 right-0 w-0 h-1 bg-[#FF5A1F] group-hover:w-full transition-all duration-500" />
                <div className="w-14 h-14 bg-[#0A1128] flex items-center justify-center group-hover:bg-[#FF5A1F] transition-colors duration-300">
                  <Icon className="w-7 h-7 text-[#FF5A1F] group-hover:text-white transition-colors duration-300" strokeWidth={2.2} />
                </div>
                <h3 className="font-display text-xl md:text-[22px] text-[#0A1128] leading-tight">
                  {t(`services.items.${k}.title`)}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t(`services.items.${k}.desc`)}
                </p>
                <span className="absolute bottom-4 right-5 text-[10px] font-black text-slate-200 tabular-nums">
                  0{i + 1}
                </span>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
